import { type FormEvent, useCallback, useEffect, useState } from "react";
import { type Api, ApiError, type BusinessProfile, type DayKey } from "../api";

const DAYS: { key: DayKey; label: string }[] = [
  { key: "monday", label: "Mon" },
  { key: "tuesday", label: "Tue" },
  { key: "wednesday", label: "Wed" },
  { key: "thursday", label: "Thu" },
  { key: "friday", label: "Fri" },
  { key: "saturday", label: "Sat" },
  { key: "sunday", label: "Sun" },
];

const SOCIAL_FIELDS = [
  ["facebook", "Facebook"],
  ["instagram", "Instagram"],
  ["x", "X"],
  ["linkedin", "LinkedIn"],
  ["youtube", "YouTube"],
  ["tiktok", "TikTok"],
] as const;

/**
 * UX-6 — the business identity behind the site: contact, address, opening
 * hours and links. Saving publishes it straight into the site's
 * LocalBusiness structured data (what Google reads for local search).
 * Google Business Profile import lands here once connected (founder TODO).
 */
export function BusinessProfileCard({ api }: { api: Api }) {
  const [profile, setProfile] = useState<BusinessProfile | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);

  const load = useCallback(async () => {
    try {
      const result = await api.getBusinessProfile();
      setProfile(result.profile);
    } catch (caught) {
      setError(
        caught instanceof ApiError
          ? caught.message
          : "Could not load your business profile.",
      );
    }
  }, [api]);

  useEffect(() => {
    void load();
  }, [load]);

  const patch = (update: Partial<BusinessProfile>) => {
    setSaved(false);
    setProfile((current) => (current ? { ...current, ...update } : current));
  };

  const setHours = (
    day: DayKey,
    opens: string | null,
    closes: string | null,
  ) => {
    setSaved(false);
    setProfile((current) => {
      if (!current) return current;
      const hours = { ...current.hours };
      if (opens && closes) {
        hours[day] = { opens, closes };
      } else {
        delete hours[day];
      }
      return { ...current, hours };
    });
  };

  const save = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!profile || saving) return;
    setSaving(true);
    setError(null);
    try {
      const result = await api.saveBusinessProfile(profile);
      setProfile(result.profile);
      setSaved(true);
    } catch (caught) {
      setError(
        caught instanceof ApiError
          ? caught.message
          : "Could not save. Try again.",
      );
    } finally {
      setSaving(false);
    }
  };

  if (!profile) {
    return (
      <section className="card business-profile-card">
        <div className="card-head">
          <div>
            <h2>Business profile</h2>
          </div>
        </div>
        {error ? (
          <p className="notice">{error}</p>
        ) : (
          <p className="muted">Loading…</p>
        )}
      </section>
    );
  }

  return (
    <section className="card business-profile-card">
      <div className="card-head">
        <div>
          <h2>Business profile</h2>
          <p className="muted">
            Address, hours and contact details — published on your site as the
            structured data Google reads for local search.
          </p>
        </div>
      </div>
      <form className="profile-form" onSubmit={save}>
        <div className="profile-grid">
          <label>
            <span>Phone</span>
            <input
              type="tel"
              placeholder="+447700900123"
              value={profile.phone ?? ""}
              onChange={(e) => patch({ phone: e.target.value || null })}
            />
          </label>
          <label>
            <span>Email</span>
            <input
              type="email"
              placeholder="hello@yourbusiness.com"
              value={profile.email ?? ""}
              onChange={(e) => patch({ email: e.target.value || null })}
            />
          </label>
          <label>
            <span>Street</span>
            <input
              type="text"
              value={profile.address.street ?? ""}
              onChange={(e) =>
                patch({
                  address: {
                    ...profile.address,
                    street: e.target.value || null,
                  },
                })
              }
            />
          </label>
          <label>
            <span>City</span>
            <input
              type="text"
              value={profile.address.city ?? ""}
              onChange={(e) =>
                patch({
                  address: { ...profile.address, city: e.target.value || null },
                })
              }
            />
          </label>
          <label>
            <span>Postcode</span>
            <input
              type="text"
              value={profile.address.postcode ?? ""}
              onChange={(e) =>
                patch({
                  address: {
                    ...profile.address,
                    postcode: e.target.value || null,
                  },
                })
              }
            />
          </label>
          <label>
            <span>Country (2-letter)</span>
            <input
              type="text"
              maxLength={2}
              placeholder="GB"
              value={profile.address.country ?? ""}
              onChange={(e) =>
                patch({
                  address: {
                    ...profile.address,
                    country: e.target.value.toUpperCase() || null,
                  },
                })
              }
            />
          </label>
        </div>

        <div className="profile-hours">
          <h3>Opening hours</h3>
          <div className="hours-grid">
            {DAYS.map(({ key, label }) => {
              const day = profile.hours[key];
              return (
                <div className="hours-row" key={key}>
                  <span className="hours-day">{label}</span>
                  <input
                    type="time"
                    value={day?.opens ?? ""}
                    onChange={(e) =>
                      setHours(key, e.target.value || null, day?.closes ?? null)
                    }
                    aria-label={`${label} opens`}
                  />
                  <span className="muted">–</span>
                  <input
                    type="time"
                    value={day?.closes ?? ""}
                    onChange={(e) =>
                      setHours(key, day?.opens ?? null, e.target.value || null)
                    }
                    aria-label={`${label} closes`}
                  />
                  <span className="muted small">
                    {day?.opens && day?.closes ? "" : "closed"}
                  </span>
                </div>
              );
            })}
          </div>
        </div>

        <div className="profile-grid">
          {SOCIAL_FIELDS.map(([key, label]) => (
            <label key={key}>
              <span>{label}</span>
              <input
                type="url"
                placeholder={`https://${key === "x" ? "x.com" : `${key}.com`}/yourbusiness`}
                value={profile.socials[key] ?? ""}
                onChange={(e) =>
                  patch({
                    socials: {
                      ...profile.socials,
                      [key]: e.target.value || null,
                    },
                  })
                }
              />
            </label>
          ))}
          <label>
            <span>Google Business Profile link</span>
            <input
              type="url"
              placeholder="https://maps.google.com/…"
              value={profile.gbpUrl ?? ""}
              onChange={(e) => patch({ gbpUrl: e.target.value || null })}
            />
          </label>
        </div>

        <div className="profile-actions">
          <button type="submit" disabled={saving}>
            {saving ? "Saving…" : saved ? "Saved — live on your site" : "Save"}
          </button>
          <span className="muted small">
            Import from Google Business Profile is coming — for now these fields
            fill it by hand.
          </span>
        </div>
        {error && <p className="notice">{error}</p>}
      </form>
    </section>
  );
}
